import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

/**
 * FOM Salesperson Leaderboard (ER-MOM-06/07 Phase 3.5) — reads upsell_offer_log
 * to rank front-office staff by upsell revenue + accepts, with top products, a
 * per-stage split, and an offered→accepted funnel. Date-range filterable.
 */
const STAGE_LABELS = { CHECKIN: 'Check-in', PRE_ARRIVAL: 'Pre-arrival', IN_STAY: 'In-stay', PRE_DEPARTURE: 'Pre-departure', '-': 'Other' };
const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isoDaysAgo = (d) => { const t = new Date(); t.setDate(t.getDate() - d); return t.toISOString().slice(0, 10); };
const medal = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`);

function PmsUpsellLeaderboard({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) { setData(null); return; }
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const res = await fetch(`/api/v1/upsell/leaderboard?${qs.toString()}`, { headers: headers() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || 'Failed to load leaderboard'); setData(null); return; }
      setData(d);
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setLoading(false); }
  }, [selectedProperty, headers, from, to]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const board = data?.leaderboard || [];
  const acceptRate = (s.sms_offered || 0) > 0
    ? Math.round(((s.funnel?.ACCEPTED || 0) / ((s.funnel?.ACCEPTED || 0) + (s.sms_offered || 0))) * 100)
    : null;

  const tile = (label, value, accent) => (
    <div style={{ flex: '1 1 160px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.7rem 0.9rem' }}>
      <div style={{ fontSize: '0.72rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '.03em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 700, color: accent || '#1a202c', marginTop: 2 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: '1rem 1.2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.9rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--theme-primary, #2d3748)' }}>⭐ Upsell Leaderboard</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', ...(isMobile ? { flexWrap: 'wrap', width: '100%' } : {}) }}>
          <label style={isMobile ? { flex: '1 1 140px', minWidth: 0, display: 'flex', alignItems: 'center' } : undefined}>From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={isMobile ? { ...inp, flex: 1, minWidth: 0 } : inp} /></label>
          <label style={isMobile ? { flex: '1 1 140px', minWidth: 0, display: 'flex', alignItems: 'center' } : undefined}>To <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={isMobile ? { ...inp, flex: 1, minWidth: 0 } : inp} /></label>
          <button onClick={load} style={isMobile ? { ...btn, width: '100%' } : btn}>Refresh</button>
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.88rem' }}>{error}</div>}
      {loading && <div style={{ color: '#718096', marginBottom: '0.6rem' }}>Loading…</div>}

      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1rem' }}>
            {tile('Upsell Revenue', money(s.total_revenue), '#166534')}
            {tile('Accepts', s.total_accepts || 0)}
            {tile('Avg Ticket', money(s.avg_ticket))}
            {tile('Top Seller', s.top_staff || '—', '#b45309')}
            {tile('SMS Nudges Sent', s.sms_offered || 0)}
            {acceptRate != null && tile('SMS Accept Rate', `${acceptRate}%`)}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
            <div style={{ flex: '2 1 420px', minWidth: 0 }}>
              <h3 style={h3}>Salesperson Ranking</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={table}>
                  <thead><tr>{['#', 'Staff', 'Accepts', 'Guests', 'Revenue'].map((h) => <th key={h} style={{ ...th, textAlign: h === 'Staff' ? 'left' : h === '#' ? 'center' : 'right' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {board.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#a0aec0' }}>No accepted upsells in this range.</td></tr>}
                    {board.map((r, i) => (
                      <tr key={r.staff} style={{ background: i % 2 ? '#f8fafc' : '#fff' }}>
                        <td style={{ ...td, textAlign: 'center', fontSize: '1.05rem' }}>{medal(i)}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{r.staff}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{r.accepts}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{r.guests}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#166534' }}>{money(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <h3 style={h3}>Top Products</h3>
              <table style={table}>
                <thead><tr><th style={{ ...th, textAlign: 'left' }}>Product</th><th style={th}>Sold</th><th style={{ ...th, textAlign: 'right' }}>Revenue</th></tr></thead>
                <tbody>
                  {(data.by_product || []).length === 0 && <tr><td colSpan={3} style={{ ...td, textAlign: 'center', color: '#a0aec0' }}>—</td></tr>}
                  {(data.by_product || []).map((p) => (
                    <tr key={p.product_name}><td style={td}>{p.product_name}</td><td style={{ ...td, textAlign: 'right' }}>{p.accepts}</td><td style={{ ...td, textAlign: 'right' }}>{money(p.revenue)}</td></tr>
                  ))}
                </tbody>
              </table>

              <h3 style={{ ...h3, marginTop: '1rem' }}>By Stage</h3>
              <table style={table}>
                <thead><tr><th style={{ ...th, textAlign: 'left' }}>Stage</th><th style={th}>Accepts</th><th style={{ ...th, textAlign: 'right' }}>Revenue</th></tr></thead>
                <tbody>
                  {(data.by_stage || []).length === 0 && <tr><td colSpan={3} style={{ ...td, textAlign: 'center', color: '#a0aec0' }}>—</td></tr>}
                  {(data.by_stage || []).map((r) => (
                    <tr key={r.stage}><td style={td}>{STAGE_LABELS[r.stage] || r.stage}</td><td style={{ ...td, textAlign: 'right' }}>{r.accepts}</td><td style={{ ...td, textAlign: 'right' }}>{money(r.revenue)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const inp = { padding: '0.3rem 0.4rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.85rem', marginLeft: 4 };
const btn = { padding: '0.35rem 0.9rem', background: 'var(--theme-primary, #2563eb)', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 };
const h3 = { margin: '0 0 0.5rem', fontSize: '0.95rem', color: '#2d3748' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' };
const th = { textAlign: 'right', padding: '0.4rem 0.55rem', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '.02em', color: '#475569' };
const td = { padding: '0.4rem 0.55rem', borderBottom: '1px solid #edf2f7', color: '#2d3748' };

export default PmsUpsellLeaderboard;
